
import { supabase } from "@/integrations/supabase/client";
import { toEdgeFunctionFailure } from "@/utils/userFacingError";
import { Receivable, TransactionSuggestion } from "@/types/payments";

export class ReceivableApiClient {
  /**
   * Get all receivables
   */
  async getReceivables(): Promise<Receivable[]> {
    try {
      console.log('ReceivableApiClient: Calling getReceivables');
      const { data, error } = await supabase.functions.invoke('receivable-api', {
        body: { action: 'getReceivables' }
      });

      if (error) {
        console.error('Error fetching receivables:', error);
        throw await toEdgeFunctionFailure(error, 'Failed to fetch receivables');
      }

      console.log('ReceivableApiClient: Received data:', data);
      return data.receivables || [];
    } catch (error) {
      console.error('Error calling receivable API:', error);
      throw error;
    }
  }

  /**
   * Get a specific receivable by ID
   */
  async getReceivable(id: string): Promise<Receivable | null> {
    try {
      console.log(`ReceivableApiClient: Calling getReceivable for ID: ${id}`);
      const { data, error } = await supabase.functions.invoke('receivable-api', {
        body: { action: 'getReceivable', id }
      });

      if (error) {
        console.error(`Error fetching receivable ${id}:`, error);
        throw await toEdgeFunctionFailure(error, 'Failed to fetch receivable');
      }

      return data.receivable || null;
    } catch (error) {
      console.error('Error calling receivable API:', error);
      throw error;
    }
  }

  /**
   * Update a receivable
   */
  async updateReceivable(id: string, receivableData: Partial<Receivable>): Promise<boolean> {
    try {
      console.log(`ReceivableApiClient: Calling updateReceivable for ID: ${id}`);
      const { data, error } = await supabase.functions.invoke('receivable-api', {
        body: { action: 'updateReceivable', id, ...receivableData }
      });

      if (error) {
        console.error(`Error updating receivable ${id}:`, error);
        throw await toEdgeFunctionFailure(error, 'Failed to update receivable');
      }

      return data.success || false;
    } catch (error) {
      console.error('Error calling receivable API:', error);
      throw error;
    }
  }

  /**
   * Match a receivable with a transaction
   */
  async matchReceivable(receivableId: string, transactionId: string): Promise<boolean> {
    try {
      console.log(`ReceivableApiClient: Calling matchReceivable for receivable: ${receivableId}, transaction: ${transactionId}`);
      const { data, error } = await supabase.functions.invoke('receivable-api', {
        body: { action: 'matchReceivable', receivableId, transactionId }
      });

      if (error) {
        console.error(`Error matching receivable ${receivableId} with transaction ${transactionId}:`, error);
        throw await toEdgeFunctionFailure(error, 'Failed to match receivable');
      }

      return data.success || false;
    } catch (error) {
      console.error('Error calling receivable API:', error);
      throw error;
    }
  }

  /**
   * Get the total pending receivables balance in the specified currency
   */
  async getPendingReceivablesBalance(targetCurrency: string = 'USD'): Promise<{ balance: number, currency: string }> {
    try {
      console.log(`ReceivableApiClient: Calling getPendingReceivablesBalance for currency: ${targetCurrency}`);
      const { data, error } = await supabase.functions.invoke('receivable-api', {
        body: { action: 'getPendingReceivablesBalance', currency: targetCurrency }
      });
      
      if (error) {
        console.error('Error fetching pending receivables balance:', error);
        throw await toEdgeFunctionFailure(error, 'Failed to fetch pending receivables balance');
      }
      
      return data;
    } catch (error) {
      console.error('Failed to fetch pending receivables balance:', error);
      throw error;
    }
  }

  // FIN-1023 AC-3: actualSekAmount is required by the backend for a non-SEK receivable
  // (BadRequestError -> 400 when missing) — same shape as PaymentApiClient's payable
  // equivalent. Omit it for a SEK receivable.
  async markAsReceivedExternally(receivableId: string, receivedDate: string, actualSekAmount?: number): Promise<boolean> {
    try {
      const { data, error } = await supabase.functions.invoke('receivable-api', {
        body: { action: 'markAsReceivedExternally', receivableId, receivedDate, actualSekAmount }
      });

      if (error) {
        console.error(`Error marking receivable ${receivableId} as received externally:`, error);
        throw await toEdgeFunctionFailure(error, 'Failed to mark receivable as received externally');
      }

      return data.success || false;
    } catch (error) {
      console.error('Error calling receivable API:', error);
      throw error;
    }
  }

  /**
   * Acknowledge a receivable as received externally when a bank account is connected but no
   * matching transaction was found (issue #498, design plan's case c — "None of these match" /
   * zero-candidate acknowledgment path). The backend derives the confirming user from the
   * authenticated session — never pass a userId here. Unlike markAsReceivedExternally above
   * (case a, trusted, no audit row), this call writes an auto_match_decisions audit row via a
   * service_role-only atomic RPC — see ReceivableService.acknowledgeExternalPayment's docstring
   * (supabase/functions/_shared/services/receivableService.ts). Mirrors
   * PaymentApiClient.acknowledgeExternalPayment's shape, minus the payable-only actualSekAmount
   * parameter (the backend action for receivables does not accept one).
   */
  async acknowledgeExternalPayment(receivableId: string, receivedDate: string): Promise<{ decisionId: string }> {
    try {
      const { data, error } = await supabase.functions.invoke('receivable-api', {
        body: { action: 'acknowledgeExternalPayment', receivableId, receivedDate }
      });

      if (error) {
        console.error(`Error acknowledging external payment for receivable ${receivableId}:`, error);
        throw await toEdgeFunctionFailure(error, 'Failed to acknowledge external payment');
      }

      return data as { decisionId: string };
    } catch (error) {
      console.error('Error calling receivable API:', error);
      throw error;
    }
  }

  async getTransactionSuggestions(receivableId: string): Promise<TransactionSuggestion[]> {
    try {
      const { data, error } = await supabase.functions.invoke('receivable-api', {
        body: { action: 'getTransactionSuggestions', receivableId }
      });

      if (error) {
        console.error(`Error fetching transaction suggestions for receivable ${receivableId}:`, error);
        throw await toEdgeFunctionFailure(error, 'Failed to fetch transaction suggestions');
      }

      return data.suggestions || [];
    } catch (error) {
      console.error('Error calling receivable API:', error);
      throw error;
    }
  }

  /**
   * Precomputed candidate transactions (issue #457) — reads the async match_jobs pipeline's
   * already-computed auto_match_decisions suggestion instead of a live JIT scoring query.
   */
  async getPrecomputedTransactionSuggestions(receivableId: string): Promise<TransactionSuggestion[]> {
    try {
      const { data, error } = await supabase.functions.invoke('receivable-api', {
        body: { action: 'getPrecomputedTransactionSuggestions', receivableId }
      });

      if (error) {
        console.error(`Error fetching precomputed transaction suggestions for receivable ${receivableId}:`, error);
        throw await toEdgeFunctionFailure(error, 'Failed to fetch precomputed transaction suggestions');
      }

      return data.suggestions || [];
    } catch (error) {
      console.error('Error calling receivable API:', error);
      throw error;
    }
  }
}
